import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface OTPVerificationProps {
  type: "phone" | "email";
  identifier: string; // phone number or email
  onVerify: (otp: string) => Promise<{ success: boolean; error?: string }>;
  onResend: () => Promise<void>;
  onBack: () => void;
  onSuccess: () => void;
}

export const OTPVerification = ({
  type,
  identifier,
  onVerify,
  onResend,
  onBack,
  onSuccess,
}: OTPVerificationProps) => {
  const { t } = useTranslation();
  // OTP codes are 6 digits for both email and phone
  const otpLength = 6;
  const [otp, setOtp] = useState<string[]>(Array(otpLength).fill(""));
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Auto-focus first input
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) {
      // Handle paste
      const pastedOtp = value.slice(0, otpLength).split("").filter(char => /^\d$/.test(char));
      const newOtp = [...otp];
      pastedOtp.forEach((digit, i) => {
        if (index + i < otpLength) {
          newOtp[index + i] = digit;
        }
      });
      setOtp(newOtp);
      
      // Focus next empty input or last
      const nextIndex = Math.min(index + pastedOtp.length, otpLength - 1);
      inputRefs.current[nextIndex]?.focus();
      
      // Auto-verify if all digits are pasted
      if (newOtp.every((digit) => digit !== "") && newOtp.join("").length === otpLength) {
        setTimeout(() => handleVerify(newOtp.join("")), 100);
      }
      return;
    }

    // Single digit input
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < otpLength - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-verify when all digits are entered
    if (newOtp.every((digit) => digit !== "") && newOtp.join("").length === otpLength) {
      setTimeout(() => handleVerify(newOtp.join("")), 100);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async (otpCode?: string) => {
    const code = otpCode || otp.join("");
    if (code.length !== otpLength) {
      toast.error(t("otp.pleaseEnterCompleteCode") || `Please enter the complete ${otpLength}-digit code`);
      return;
    }

    setIsVerifying(true);
    try {
      const result = await onVerify(code);
      if (result.success) {
        toast.success(t("otp.verificationSuccessful"));
        onSuccess();
      } else {
        toast.error(result.error || t("otp.invalidCode"));
        // Clear OTP on error
        setOtp(Array(otpLength).fill(""));
        inputRefs.current[0]?.focus();
      }
    } catch (error) {
      toast.error(t("otp.verificationFailed"));
      setOtp(Array(otpLength).fill(""));
      inputRefs.current[0]?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;

    setIsResending(true);
    try {
      await onResend();
      setResendCooldown(30); // 30 second cooldown (reduced from 60)
      toast.success(`${t("otp.codeSentToIdentifier")} ${identifier}`);
    } catch (error) {
      toast.error(t("otp.failedToResend"));
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="w-5 h-5" />
        <span>{t("entry.back")}</span>
      </button>

      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-foreground mb-2">{t("otp.enterVerificationCode")}</h2>
        <p className="text-sm text-muted-foreground">
          {t("otp.codeSentTo")}{" "}
          <span className="font-medium text-foreground">{identifier}</span>
        </p>
        {type === "phone" && (
          <>
            <p className="text-xs text-muted-foreground mt-2">
              {t("otp.smsMayTakeMoment")}
            </p>
            <div className="text-xs text-amber-600 dark:text-amber-400 mt-2 p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200 dark:border-amber-800">
              <p className="font-semibold mb-1">⚠️ Not receiving SMS?</p>
              <p className="mb-2">Most common issue: Twilio trial accounts can ONLY send to verified numbers.</p>
              <a 
                href="https://console.twilio.com/us1/develop/phone-numbers/manage/verified" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-amber-700 dark:text-amber-300 underline font-medium hover:text-amber-800 dark:hover:text-amber-200"
              >
                → Verify your phone number in Twilio Console
              </a>
              <p className="mt-2 text-xs">
                See browser console (F12) for detailed troubleshooting steps.
              </p>
            </div>
          </>
        )}
        {type === "email" && (
          <p className="text-xs text-muted-foreground mt-2">
            {t("otp.checkSpamFolder")}
          </p>
        )}
      </div>

      {/* OTP Input */}
      <div className="flex justify-center gap-2 flex-wrap max-w-md mx-auto">
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            value={digit}
            onChange={(e) => handleOtpChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={(e) => {
              e.preventDefault();
              const pastedData = e.clipboardData.getData("text");
              if (pastedData) {
                handleOtpChange(index, pastedData);
              }
            }}
            className={cn(
              "w-12 h-14 text-center text-2xl font-bold rounded-xl",
              "bg-muted border-2 border-transparent",
              "focus:border-[hsl(var(--romantic-primary))] focus:ring-2 focus:ring-[hsl(var(--romantic-primary))]/20",
              "transition-all outline-none",
              "disabled:opacity-50 disabled:cursor-not-allowed"
            )}
            disabled={isVerifying}
            aria-label={`OTP digit ${index + 1}`}
          />
        ))}
      </div>

      {/* Verify Button */}
      <button
        onClick={() => handleVerify()}
        disabled={isVerifying || otp.join("").length !== otpLength}
        className={cn(
          "w-full py-4 rounded-xl bg-gradient-to-r from-[hsl(var(--romantic-primary))] to-[hsl(var(--romantic-accent))] text-white font-semibold",
          "shadow-[0_10px_40px_-10px_hsl(var(--romantic-primary)/0.25%)]",
          "hover:shadow-[0_15px_50px_-10px_hsl(var(--romantic-primary)/0.35%)]",
          "hover:scale-[1.02] active:scale-[0.98] transition-all",
          "disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        )}
      >
        {isVerifying && <Loader2 className="w-5 h-5 animate-spin" />}
        {isVerifying ? t("otp.verifying") : t("otp.verify")}
      </button>

      {/* Resend Code */}
      <div className="text-center">
        <p className="text-sm text-muted-foreground">
          {t("otp.didntReceiveCode")}{" "}
          <button
            onClick={handleResend}
            disabled={isResending || resendCooldown > 0}
            className={cn(
              "text-[hsl(var(--romantic-primary))] font-semibold hover:underline",
              (isResending || resendCooldown > 0) && "opacity-50 cursor-not-allowed"
            )}
          >
            {isResending
              ? t("entry.sending")
              : resendCooldown > 0
              ? `${t("otp.resendIn")} ${resendCooldown}s`
              : t("otp.resendCode")}
          </button>
        </p>
      </div>
    </div>
  );
};

